import Type from 'typebox'
import { SchemaRegistry } from '../schemaRegistry';

export const registry: Omit<SchemaRegistry, 'schema'> = {
  id: 'cmdb_application_schema-v1',
  version: '1.0.0',
  title: 'CMDB Application Schema',
  description: 'Defines the structure for a single application record from the CMDB, with a unified infrastructure model.'
}

// --- Common Attribute Definitions ---
const CommonProps = {
  name: Type.String({ description: "A human-readable name for the resource (e.g., 'prod-db-01')." }),
  environment: Type.String({ description: "The deployment environment (e.g., 'production', 'staging')." }),
  install_status: Type.String({ description: "The current installation or operational status of the resource." }),
};

const DatabaseProps = {
  alias: Type.String({ description: "An alternative name or CNAME for the database." }),
};

const ServerProps = {
  host_name: Type.String({ description: "The fully qualified domain name (FQDN) of the server." }),
  ip_address: Type.String({ format: "ipv4", description: "The primary IPv4 address of the server." }),
  os_type: Type.String({ description: "The operating system running on the server (e.g., 'Linux', 'Windows Server')." }),
};

const LoadBalancerProps = {
  dns_name: Type.String({ description: "The public or internal DNS name of the load balancer." }),
  listener_port: Type.Integer({ description: "The port the load balancer is listening on." }),
};

const ComputeProps = {
  instance_type: Type.String({ description: "The instance size or type (e.g., 't3.medium', 'Standard_D2s_v3')." }),
  image_id: Type.String({ description: "The ID of the machine image or template used." }),
};

const StorageProps = {
  storage_type: Type.String({ description: "The type of storage (e.g., 'SSD', 'HDD', 'Object Storage')." }),
  capacity_gb: Type.Integer({ description: "The total capacity of the storage volume in gigabytes." }),
};

const CacheProps = {
  engine: Type.String({ description: "The caching engine used (e.g., 'Redis', 'Memcached')." }),
  node_type: Type.String({ description: "The instance size for the cache nodes." }),
};

const MiddlewareProps = {
  middleware_type: Type.String({ description: "The type of middleware (e.g., 'Message Broker', 'Application Server', 'Integration Platform')." }),
  vendor: Type.String({ description: "The vendor or provider of the middleware (e.g., 'IBM', 'Oracle', 'Apache')." }),
  version: Type.String({ description: "The version of the middleware." }),
};

const VpcProps = {
  cidr_block: Type.String({ description: "The CIDR block for the VPC (e.g., '10.0.0.0/16')." }),
};

const SecurityGroupProps = {
  description: Type.String({ description: "A summary of the security group's purpose." }),
};

const ApiGatewayProps = {
  endpoint_type: Type.Enum(
    { Regional: "Regional", EdgeOptimized: "Edge-Optimized", Private: "Private" },
    { description: "The type of API endpoint." }
  ),
};

const ContainerOrchestratorProps = {
  orchestrator_type: Type.Enum(
    { Kubernetes: "Kubernetes", ECS: "ECS", Swarm: "Swarm" },
    { description: "The container orchestration platform." }
  ),
};

const MessageQueueProps = {
  queue_type: Type.Enum(
    { Standard: "Standard", FIFO: "FIFO" },
    { description: "The type of message queue." }
  ),
};

const IamRoleProps = {
  role_arn: Type.String({ description: "The Amazon Resource Name (ARN) of the IAM role." }),
};

// --- Infrastructure Item Union ---
const InfrastructureItem = Type.Union([
  Type.Object({
    resource_type: Type.Literal("Database", { title: "Resource Type" }),
    ...CommonProps,
    ...DatabaseProps,
  }, { title: "Database", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("Server", { title: "Resource Type" }),
    ...CommonProps,
    ...ServerProps,
  }, { title: "Server", required: ["resource_type", "name", "host_name"] }),
  Type.Object({
    resource_type: Type.Literal("LoadBalancer", { title: "Resource Type" }),
    ...CommonProps,
    ...LoadBalancerProps,
  }, { title: "LoadBalancer", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("Compute", { title: "Resource Type" }),
    ...CommonProps,
    ...ComputeProps,
  }, { title: "Compute", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("Storage", { title: "Resource Type" }),
    ...CommonProps,
    ...StorageProps,
  }, { title: "Storage", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("Cache", { title: "Resource Type" }),
    ...CommonProps,
    ...CacheProps,
  }, { title: "Cache", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("Middleware", { title: "Resource Type" }),
    ...CommonProps,
    ...MiddlewareProps,
  }, { title: "Middleware", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("VPC", { title: "Resource Type" }),
    ...CommonProps,
    ...VpcProps,
  }, { title: "VPC", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("SecurityGroup", { title: "Resource Type" }),
    ...CommonProps,
    ...SecurityGroupProps,
  }, { title: "Security Group", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("APIGateway", { title: "Resource Type" }),
    ...CommonProps,
    ...ApiGatewayProps,
  }, { title: "API Gateway", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("ContainerOrchestrator", { title: "Resource Type" }),
    ...CommonProps,
    ...ContainerOrchestratorProps,
  }, { title: "Container Orchestrator", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("MessageQueue", { title: "Resource Type" }),
    ...CommonProps,
    ...MessageQueueProps,
  }, { title: "Message Queue", required: ["resource_type", "name"] }),
  Type.Object({
    resource_type: Type.Literal("IAMRole", { title: "Resource Type" }),
    ...CommonProps,
    ...IamRoleProps,
  }, { title: "IAM Role", required: ["resource_type", "name"] }),
]);

const TierEnum = {
  Tier0: "Tier 0",
  Tier1: "Tier 1",
  Tier2: "Tier 2",
  Tier3: "Tier 3",
  Tier4: "Tier 4",
  Tier5: "Tier 5",
  Tier6: "Tier 6",
};

// --- Main Schema ---
const definition = Type.Object({
  app_id: Type.String({ description: "The unique identifier for the service or application." }),
  app_name: Type.String({ description: "The primary, user-facing name of the application." }),
  app_description: Type.String({ description: "A brief summary of the application's purpose." }),
  app_type: Type.String({ description: "The type of application (e.g. On-Premise, Cloud)." }),
  tier: Type.Enum(TierEnum, { description: "The business criticality tier of the application." }),
  lob: Type.String({ description: "The line of business the application belongs to." }),
  division: Type.String({ description: "The division the application belongs to." }),
  app_owner: Type.String({ description: "The primary technical contact or owner." }),
  business_owner: Type.String({ description: "The primary business stakeholder." }),
  support_l1_owner: Type.String({ description: "The Level 1 support contact." }),
  support_l2_owner: Type.String({ description: "The Level 2 support contact." }),
  support_l3_owner: Type.String({ description: "The Level 3 support contact." }),
  rto: Type.Integer({ description: "The Recovery Time Objective for the application in minutes." }),
  rpo: Type.Integer({ description: "The Recovery Point Objective for the application in minutes." }),
  status: Type.String({ description: "The current operational status of the application." }),
  datacenter_location: Type.String({ description: "The physical data center location." }),
  location: Type.String({ description: "The geographical region, e.g., US, EMEA." }),
  cloud_primary_location: Type.String({ description: "The primary cloud location for the application." }),
  cloud_secondary_location: Type.String({ description: "The secondary cloud location for the application." }),
  review_date: Type.String({ description: "The last review date for this record.", format: "date-time" }),
  soc1: Type.Enum({ YES: "YES", NO: "NO" }),
  soc2: Type.Enum({ YES: "YES", NO: "NO" }),
  sox: Type.Enum({ YES: "YES", NO: "NO" }),
  infrastructure: Type.Array(InfrastructureItem, {
    title: "Application Infrastructure",
    description: "A list of all associated infrastructure resources for the application.",
  }),
}, { required: ["app_id", "app_name"] });

export const CmdbApplicationSchemaV1 = { registry, definition };
